/**
 * Utility functions for handling media device permissions
 */

/**
 * Check if the browser has access to media devices (camera and microphone)
 * @param {Object} options - Options for checking permissions
 * @param {boolean} options.audio - Whether to check for audio permission
 * @param {boolean} options.video - Whether to check for video permission
 * @returns {Promise<Object>} Result object with permission status
 */
export const checkMediaPermissions = async (options = { audio: true, video: true }) => {
    const result = {
        granted: false,
        error: null,
        errorType: null,
        errorMessage: null,
        permissions: {
            camera: 'unknown',
            microphone: 'unknown'
        }
    };

    try {
        // First try to use the Permissions API if available
        if (navigator.permissions) {
            try {
                if (options.video) {
                    const cameraPermission = await navigator.permissions.query({ name: 'camera' });
                    result.permissions.camera = cameraPermission.state;
                }

                if (options.audio) {
                    const microphonePermission = await navigator.permissions.query({ name: 'microphone' });
                    result.permissions.microphone = microphonePermission.state;
                }

                // If both permissions are granted, we're good
                if ((!options.video || result.permissions.camera === 'granted') &&
                    (!options.audio || result.permissions.microphone === 'granted')) {
                    result.granted = true;
                    return result;
                }
            } catch (err) {
                // Permissions API might not be fully supported, fall back to getUserMedia
                console.warn('Permissions API not fully supported:', err);
            }
        }

        // Try to access devices using getUserMedia
        const constraints = {
            audio: options.audio,
            video: options.video
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // If we get here, permissions were granted
        result.granted = true;

        // Close all tracks to release devices
        stream.getTracks().forEach(track => track.stop());

        return result;
    } catch (error) {
        result.granted = false;
        result.error = error;
        result.errorType = error.name;

        // Set appropriate error message based on the error type
        switch (error.name) {
            case 'NotAllowedError':
            case 'PermissionDeniedError':
                result.errorMessage = 'Permission to access camera and/or microphone was denied.';
                break;
            case 'NotFoundError':
                result.errorMessage = 'No camera or microphone found on this device.';
                break;
            case 'NotReadableError':
            case 'TrackStartError':
                result.errorMessage = 'Your camera or microphone is already in use by another application.';
                break;
            case 'OverconstrainedError':
                result.errorMessage = 'The requested media device settings are not available on this device.';
                break;
            case 'TypeError':
                result.errorMessage = 'No media tracks were requested.';
                break;
            default:
                result.errorMessage = `Unable to access media devices: ${error.message}`;
        }

        return result;
    }
};

/**
 * Get instructions on how to fix permission issues based on the browser
 * @returns {Object} Browser-specific instructions
 */
export const getPermissionInstructions = () => {
    // Detect browser
    const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
    const isFirefox = navigator.userAgent.indexOf('Firefox') > -1;
    const isSafari = navigator.userAgent.indexOf('Safari') > -1 && !isChrome;
    const isEdge = navigator.userAgent.indexOf('Edg') > -1;

    const instructions = {
        general: [
            'Try refreshing the page after changing permissions.',
            'Make sure no other applications are using your camera or microphone.'
        ],
        browser: []
    };

    if (isChrome || isEdge) {
        instructions.browser = [
            'Click the camera icon in the address bar.',
            'Select "Always allow" for both camera and microphone.',
            'Refresh the page.'
        ];
    } else if (isFirefox) {
        instructions.browser = [
            'Click the camera icon in the address bar.',
            'Click "More Information" to access site permissions.',
            'Set both Camera and Microphone to "Allow".',
            'Refresh the page.'
        ];
    } else if (isSafari) {
        instructions.browser = [
            'Go to Safari > Preferences > Websites > Camera/Microphone.',
            'Find this website and set permissions to "Allow".',
            'Refresh the page.'
        ];
    } else {
        instructions.browser = [
            'Look for camera/microphone permissions in your browser settings.',
            'Allow access for this website.',
            'Refresh the page.'
        ];
    }

    return instructions;
};

export default {
    checkMediaPermissions,
    getPermissionInstructions
};
